import { useEffect, useState, useRef, useCallback } from "react";

const useIdleTimeout = (logout, isAuthenticated, timeoutMinutes = 30) => {
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const timeoutMs = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
  const timeoutIdRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    const expiryDate = Date.now() + timeoutMs;
    localStorage.setItem("expiryDate", expiryDate.toString());
    localStorage.setItem("lastActiveDate", Date.now().toString());
    
    timeoutIdRef.current = setTimeout(() => {
      setShowTimeoutPopup(true);
    }, timeoutMs);
  }, [timeoutMs]);

  const handleUserActivity = useCallback(() => {
    if (isAuthenticated) {
      resetTimer();
    }
  }, [isAuthenticated, resetTimer]);

  useEffect(() => {
    if (isAuthenticated) {
      // Check if there's an existing expiry date
      const expiryDate = localStorage.getItem("expiryDate");
      const lastActiveDate = localStorage.getItem("lastActiveDate");
      
      if (expiryDate && lastActiveDate) {
        const now = Date.now();
        const timeLeft = parseInt(expiryDate) - now;
        
        if (timeLeft <= 0) {
          // If time has expired, clear the stored dates and reset timer
          localStorage.removeItem("expiryDate");
          localStorage.removeItem("lastActiveDate");
          resetTimer();
        } else {
          timeoutIdRef.current = setTimeout(() => {
            setShowTimeoutPopup(true);
          }, timeLeft);
        }
      } else {
        // If no stored dates, start fresh
        resetTimer();
      }

      // Add event listeners for user activity
      window.addEventListener("mousemove", handleUserActivity);
      window.addEventListener("keydown", handleUserActivity);
      window.addEventListener("click", handleUserActivity);
      window.addEventListener("scroll", handleUserActivity);
      window.addEventListener("touchstart", handleUserActivity);
    }

    // Listen for logout event from other tabs
    const handleStorageChange = (event) => {
      if (event.key === "logout") {
        setShowTimeoutPopup(false);
        logout();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("touchstart", handleUserActivity);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAuthenticated, handleUserActivity, resetTimer, logout]);

  return { showTimeoutPopup, setShowTimeoutPopup };
};

export default useIdleTimeout;
